import _, { Dictionary } from 'lodash';

export interface SupportedChains {
  chainId: number;
  isTestnet: boolean;
  name: string;
}

interface ChainConfig {
  fuulFactoryContractAddress: string;
  fuulManagerContractAddress: string;
  fuulSubgraphApiUrl: string;
  fuulClientFeeCollectorAddress: string;
}

interface NetworkConfig {
  [chainId: number]: ChainConfig & SupportedChains;
}

// Chains with V2 contracts deployed
export const supportedChainIds: SupportedChains[] = [
  // V2 deployed - active
  {
    chainId: 42161,
    name: 'Arbitrum',
    isTestnet: false,
  },
  {
    chainId: 8453,
    name: 'Base',
    isTestnet: false,
  },
  {
    chainId: 999,
    name: 'HyperEVM',
    isTestnet: false,
  },
  {
    chainId: 10,
    name: 'Optimism',
    isTestnet: false,
  },
  {
    chainId: 84532,
    name: 'Base Sepolia',
    isTestnet: true,
  },
  {
    chainId: 1,
    name: 'Ethereum Mainnet',
    isTestnet: false,
  },
  {
    chainId: 57073,
    name: 'Ink',
    isTestnet: false,
  },
  {
    chainId: 143,
    name: 'Monad',
    isTestnet: false,
  },
  // V2 not yet deployed - uncomment when ready
  // ...(process.env.NEXT_PUBLIC_HARDHAT_NETWORK ? [{ chainId: 31337, name: 'Localhost', isTestnet: true }] : []),
  // {
  //   chainId: 42,
  //   name: 'LUKSO Mainnet',
  //   isTestnet: false,
  // },
  // {
  //   chainId: 137,
  //   name: 'Polygon Mainnet',
  //   isTestnet: false,
  // },
  // {
  //   chainId: 56,
  //   name: 'BNB',
  //   isTestnet: false,
  // },
  // {
  //   chainId: 324,
  //   name: 'zkSync',
  //   isTestnet: false,
  // },
  // {
  //   chainId: 34443,
  //   name: 'Mode',
  //   isTestnet: false,
  // },
  // {
  //   chainId: 2741,
  //   name: 'Abstract',
  //   isTestnet: false,
  // },
  // {
  //   chainId: 10143,
  //   name: 'Monad Testnet',
  //   isTestnet: true,
  // },
];

export const supportedTestnetsChainIds = supportedChainIds
  .filter((chain) => chain.isTestnet)
  .map((chain) => chain.chainId);
export const supportedMainnetsChainIds = supportedChainIds
  .filter((chain) => !chain.isTestnet)
  .map((chain) => chain.chainId);

const grouppedSupportedChainIds: Dictionary<SupportedChains[]> = _.groupBy(supportedChainIds, 'chainId');

// TODO: Refactor these process.env objects into a centralized configuration system
// Consider creating a config service that validates all environment variables at startup

// V1 Factory Contract Addresses (used for affiliate portal claims via subgraph)
// These use the _V1 suffix env vars which contain the deployed V1 contract addresses
const fuulFactoryContractAddress: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_1_V1,
  42: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_42_V1,
  137: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_137_V1,
  8453: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_8453_V1,
  10: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_10_V1,
  42161: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_42161_V1,
  56: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_56_V1,
  84532: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_84532_V1,
  324: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_324_V1,
  34443: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_34443_V1,
  2741: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_2741_V1,
  10143: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_10143_V1,
  999: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_999_V1,
  57073: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_57073_V1,
  143: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_143_V1,
  31337: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_31337_V1,
};

// V1 Manager Contract Addresses (used for affiliate portal)
const fuulManagerContractAddress: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_1_V1,
  42: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_42_V1,
  137: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_137_V1,
  8453: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_8453_V1,
  10: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_10_V1,
  42161: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_42161_V1,
  56: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_56_V1,
  84532: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_84532_V1,
  324: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_324_V1,
  34443: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_34443_V1,
  2741: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_2741_V1,
  10143: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_10143_V1,
  999: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_999_V1,
  57073: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_57073_V1,
  143: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_143_V1,
  31337: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_31337_V1,
};

// V1 Client Fee Collector Addresses
const fuulClientFeeCollectorAddress: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_1_V1,
  42: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_42_V1,
  137: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_137_V1,
  8453: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_8453_V1,
  10: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_10_V1,
  42161: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_42161_V1,
  56: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_56_V1,
  84532: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_84532_V1,
  324: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_324_V1,
  34443: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_34443_V1,
  2741: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_2741_V1,
  10143: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_10143_V1,
  999: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_999_V1,
  57073: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_57073_V1,
  143: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_143_V1,
  31337: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_31337_V1,
};

// V1 Subgraph API URLs (index V1 contracts for balance queries)
const fuulSubgraphApiUrl: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_1_V1,
  42: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_42_V1,
  137: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_137_V1,
  8453: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_8453_V1,
  10: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_10_V1,
  42161: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_42161_V1,
  56: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_56_V1,
  84532: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_84532_V1,
  324: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_324_V1,
  34443: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_34443_V1,
  2741: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_2741_V1,
  10143: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_10143_V1,
  999: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_999_V1,
  57073: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_57073_V1,
  143: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_143_V1,
  31337: process.env.NEXT_PUBLIC_FUUL_FACTORY_SUBGRAPH_API_URL_31337_V1,
};

////////////////////////////////////////////////////////////
// V2 Contract Addresses (used for NEW project deployments)
// These use the original env var names (without _V1 suffix)
////////////////////////////////////////////////////////////

// V2 Factory Contract Addresses (used for deploying new projects)
const fuulFactoryContractAddressV2: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_1,
  42: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_42,
  137: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_137,
  8453: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_8453,
  10: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_10,
  42161: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_42161,
  56: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_56,
  84532: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_84532,
  324: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_324,
  34443: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_34443,
  2741: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_2741,
  10143: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_10143,
  999: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_999,
  57073: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_57073,
  143: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_143,
  31337: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_31337,
};

// V2 Manager Contract Addresses
const fuulManagerContractAddressV2: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_1,
  42: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_42,
  137: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_137,
  8453: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_8453,
  10: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_10,
  42161: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_42161,
  56: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_56,
  84532: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_84532,
  324: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_324,
  34443: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_34443,
  2741: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_2741,
  10143: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_10143,
  999: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_999,
  57073: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_57073,
  143: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_143,
  31337: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_31337,
};

// V2 Client Fee Collector Addresses
const fuulClientFeeCollectorAddressV2: Record<number, string | undefined> = {
  1: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_1,
  42: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_42,
  137: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_137,
  8453: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_8453,
  10: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_10,
  42161: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_42161,
  56: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_56,
  84532: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_84532,
  324: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_324,
  34443: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_34443,
  2741: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_2741,
  10143: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_10143,
  999: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_999,
  57073: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_57073,
  143: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_143,
  31337: process.env.NEXT_PUBLIC_CLIENT_FEE_COLLECTOR_ADDRESS_31337,
};
////////////////////////////////////////////////////////////

// V1 network config - used for claims on existing projects (uses V1 subgraph)
export const networksConfigV1: NetworkConfig = _.mapValues(grouppedSupportedChainIds, (chains) => {
  const chain = chains[0];
  const chainId = chain.chainId;
  return {
    ...chain,
    fuulFactoryContractAddress: fuulFactoryContractAddress[chainId] as string,
    fuulManagerContractAddress: fuulManagerContractAddress[chainId] as string,
    fuulClientFeeCollectorAddress: fuulClientFeeCollectorAddress[chainId] as string,
    fuulSubgraphApiUrl: fuulSubgraphApiUrl[chainId] as string,
  };
});

// V1 network config getter - used for claims (existing projects)
export const getNetworkConfigV1 = (chainId: string | number | null | undefined): ChainConfig => {
  if (chainId === null || chainId === undefined) return {} as ChainConfig;
  const numericChainId = typeof chainId === 'string' ? Number(chainId) : chainId;
  return networksConfigV1[numericChainId] ?? {};
};

// V2 network config (current) - used for NEW project deployments
export const networksConfig: NetworkConfig = _.mapValues(grouppedSupportedChainIds, (chains) => {
  const chain = chains[0];
  const chainId = chain.chainId;
  return {
    ...chain,
    fuulFactoryContractAddress: fuulFactoryContractAddressV2[chainId] as string,
    fuulManagerContractAddress: fuulManagerContractAddressV2[chainId] as string,
    fuulClientFeeCollectorAddress: fuulClientFeeCollectorAddressV2[chainId] as string,
    fuulSubgraphApiUrl: fuulSubgraphApiUrl[chainId] as string, // V2 will use same subgraph once available
  };
});

// V2 network config getter (current) - used for deploying NEW projects
export const getNetworkConfig = (chainId: string | number | null | undefined): ChainConfig => {
  if (chainId === null || chainId === undefined) return {} as ChainConfig;
  const numericChainId = typeof chainId === 'string' ? Number(chainId) : chainId;
  return networksConfig[numericChainId] ?? {};
};

export const getMainnetNetworksConfig = () => {
  return supportedMainnetsChainIds.map((chainId) => networksConfig[chainId]);
};

export const customNetworksForDynamic = [
  // Base network - First entry becomes default network for email login
  {
    blockExplorerUrls: ['https://basescan.org/'],
    chainId: 8453,
    chainName: 'Base',
    iconUrls: ['/assets/svg/base.svg'],
    name: 'Base',
    nativeCurrency: {
      decimals: 18,
      name: 'Ethereum',
      symbol: 'ETH',
      iconUrl: '/assets/svg/eth.svg',
    },
    networkId: 8453,
    rpcUrls: ['https://mainnet.base.org', 'https://base-mainnet.g.alchemy.com/v2/demo', 'https://base.drpc.org'],
    vanityName: 'Base Mainnet',
  },
  // Ink network
  {
    blockExplorerUrls: ['https://explorer.inkonchain.com/'],
    chainId: 57073,
    chainName: 'Ink',
    iconUrls: ['/assets/svg/ink.svg'],
    name: 'Ink',
    nativeCurrency: {
      decimals: 18,
      name: 'Ethereum',
      symbol: 'ETH',
      iconUrl: '/assets/svg/eth.svg',
    },
    networkId: 57073,
    rpcUrls: ['https://rpc-gel.inkonchain.com'],
    vanityName: 'Ink Mainnet',
  },
  // Monad network
  {
    blockExplorerUrls: ['https://monadscan.com'],
    chainId: 143,
    chainName: 'Monad',
    iconUrls: ['/assets/svg/monad.svg'],
    name: 'Monad',
    nativeCurrency: {
      decimals: 18,
      name: 'Monad',
      symbol: 'MON',
    },
    networkId: 143,
    rpcUrls: ['https://rpc.monad.xyz'],
    vanityName: 'Monad Mainnet',
  },
  // LUKSO network - Previously first, now second
  {
    blockExplorerUrls: ['https://explorer.execution.mainnet.lukso.network/'],
    chainId: 42,
    chainName: 'LUKSO Mainnet',
    iconUrls: ['/assets/svg/lukso.svg'],
    name: 'LUKSO',
    nativeCurrency: {
      decimals: 18,
      name: 'LYX',
      symbol: 'LYX',
      iconUrl: '/assets/svg/lukso.svg',
    },
    networkId: 42,
    rpcUrls: [
      'https://rpc.lukso.sigmacore.io',
      'https://rpc.mainnet.lukso.network',
      'wss://ws-rpc.mainnet.lukso.network',
      'https://42.rpc.thirdweb.com',
    ],
    vanityName: 'LUKSO Mainnet',
  },
  ...(process.env.NEXT_PUBLIC_HARDHAT_NETWORK
    ? [
        {
          chainId: 31337,
          networkId: 31337,
          name: 'Localhost',
          rpcUrls: ['http://localhost:8545'],
          blockExplorerUrls: ['https://etherscan.io'],
          iconUrls: ['https://avatars.githubusercontent.com/u/6250754'],
          nativeCurrency: {
            name: 'Token',
            symbol: 'TOK',
            decimals: 18,
            iconUrl: 'https://avatars.githubusercontent.com/u/6250754',
          },
        },
      ]
    : []),
];
